import { DateTime } from 'luxon';
import * as R from 'ramda';

export {
  parseAndOrderWeekdays,
  getCurrentWeekdayOpenHours,
  isOpen,
  getOpenTimeLabel,
  canParseOperationHours,
  toLocalDate,
  getBoundaryMinutes,
};

export type { TimeOfDay, TimePeriod, WeekdayPeriod };

/** Translation function, matching the shape supplied by the plugin runtime. */
type TranslateFn = (key: string, options?: Record<string, unknown>) => string;

/** A wall-clock time of day, with no date or timezone attached. */
interface TimeOfDay {
  hours: number;
  minutes: number;
}

/** An open period for a single weekday, plus its display-formatted label. */
interface TimePeriod {
  from: TimeOfDay;
  to: TimeOfDay;
  formatted: string;
}

/** A weekday paired with the hours it is open. `isToday` is set by {@link markCurrentWeekday}. */
interface WeekdayPeriod {
  weekday: string;
  timePeriod: TimePeriod;
  isToday?: boolean;
}

function getCurrentWeekdayOpenHours(weekdayPeriods: WeekdayPeriod[], today: Date): string {
  const currentWeekday = findWeekday(weekdayPeriods, today);
  return currentWeekday ? currentWeekday.timePeriod.formatted : '';
}

/**
 * Checks if operationHours can be parsed into weekday periods.
 * Expects "Mo-Fr 09:00-17:00; Sa 10:00-14:00" (24-hour) format, and returns false for freeform strings.
 */
function canParseOperationHours(operationHours: string | undefined | null): boolean {
  if (!operationHours || typeof operationHours !== 'string') {
    return false;
  }

  const trimmed = operationHours.trim();
  if (trimmed === '') {
    return false;
  }

  // Valid format is "Mo-Fr 09:00-17:00" or similar
  // The regex requires a weekday abbreviation at the start, so freeform text
  // like "24/7", "open daily", etc. will naturally fail validation
  const validDayPattern = /^(Su|Mo|Tu|We|Th|Fr|Sa)(-(?:Su|Mo|Tu|We|Th|Fr|Sa))?\s+\d{1,2}:\d{2}-\d{1,2}:\d{2}$/;
  const segments = trimmed.split(';').map(s => s.trim());

  // If any segment fails to match the pattern, we can't parse the whole string
  return segments.every(segment => validDayPattern.test(segment));
}

function isOpen(weekdayPeriods: WeekdayPeriod[], today: Date): boolean {
  const currentWeekday = findWeekday(weekdayPeriods, today);
  if (!currentWeekday) {
    return false;
  }

  const hours = today.getHours();
  const minutes = today.getMinutes();

  const { from, to } = currentWeekday.timePeriod;
  return isInTimePeriod(hours, minutes, from.hours, from.minutes, to.hours, to.minutes);
}

/**
 * Minutes from `localizedNow` until today's opening and closing times.
 *
 * Both values are relative to the same day, so a negative number means the boundary has
 * already passed. Returns `undefined` for both when the POI has no hours for today.
 *
 * Exposed so callers can derive "opening soon" / "closing soon" states without
 * inspecting a translated label. `localizedNow` must already be in venue-local time
 * (see {@link toLocalDate}).
 */
function getBoundaryMinutes(
  weekdayPeriods: WeekdayPeriod[],
  localizedNow: Date,
): { minutesToOpen: number | undefined; minutesToClose: number | undefined } {
  const currentWeekday = findWeekday(weekdayPeriods, localizedNow);
  if (!currentWeekday) {
    return { minutesToOpen: undefined, minutesToClose: undefined };
  }

  return {
    minutesToOpen: minutesBetweenDateTimeAndWeekdayTime(localizedNow, currentWeekday.timePeriod.from),
    minutesToClose: minutesBetweenDateTimeAndWeekdayTime(localizedNow, currentWeekday.timePeriod.to),
  };
}

function isInTimePeriod(
  nowHours: number,
  nowMinutes: number,
  fromHours: number,
  fromMinutes: number,
  toHours: number,
  toMinutes: number,
): boolean {
  const isOvernightPeriod = isFirstTimeAfterSecond(fromHours, fromMinutes, toHours, toMinutes);
  if (isOvernightPeriod) {
    return (
      isInTimePeriod(nowHours, nowMinutes, fromHours, fromMinutes, 24, 0) ||
      isInTimePeriod(nowHours, nowMinutes, 0, 0, toHours, toMinutes)
    );
  } else {
    const isAfterFrom = isFirstTimeAfterSecond(nowHours, nowMinutes, fromHours, fromMinutes);
    const isBeforeTo = isFirstTimeAfterSecond(toHours, toMinutes, nowHours, nowMinutes);
    return isAfterFrom && isBeforeTo;
  }
}

function isFirstTimeAfterSecond(
  firstHours: number,
  firstMinutes: number,
  secondHours: number,
  secondMinutes: number,
): boolean {
  return firstHours > secondHours || (firstHours === secondHours && firstMinutes >= secondMinutes);
}

function findWeekday(weekdayPeriods: WeekdayPeriod[], today: Date): WeekdayPeriod | undefined {
  return findWeekdayByDayIndex(weekdayPeriods, today.getDay());
}

function findWeekdayByDayIndex(weekdayPeriods: WeekdayPeriod[], index: number): WeekdayPeriod | undefined {
  const currentWeekdayName = WEEKDAY_ARRAY[index];
  return weekdayPeriods.find(({ weekday }) => weekday === currentWeekdayName);
}

/**
 Mo-Su 08:00-17:00
 Mo 07:00-01:00; Tu 07:00-03:00; We 07:00-01:00; Th 07:00-03:00; Fr-Sa 07:00-04:30; Su 07:00-03:00
 Mo-Su 12:00-02:00
 Su-Th 10:00-18:00; Fr-Sa 09:00-19:00
 We-Su 17:30-21:30
 Su-Th 10:00-23:00; Fr-Sa 10:00-24:00
 Mo-Th 05:00-10:30; Fr-Su 09:00-22:45
 Su-Th 10:00-23:00; Fr-Sa 10:00-24:00,

 output:
 [ {
    weekday: 'Sunday',
    isToday: true
    timePeriod: {
      from: {
        hours: 8,
        minutes: 0
      },
      to: {
        hours: 23,
        minutes: 0
      },
      formatted: '8:00am-11:00pm'
    }, ...remaining days ]
 */
function parseAndOrderWeekdays(rawWeekdayPeriods: string, today: Date): WeekdayPeriod[] {
  const weekdays = parseWeekdayPeriods(rawWeekdayPeriods);
  const orderedWeekdays = orderFromSunday(weekdays);
  return markCurrentWeekday(orderedWeekdays, today);
}

const SHORT_WEEKDAY_TO_NUMERAL: Record<string, number> = {
  Su: 0,
  Mo: 1,
  Tu: 2,
  We: 3,
  Th: 4,
  Fr: 5,
  Sa: 6,
};
const WEEKDAY_ARRAY = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const WEEKDAY_TO_NUMERAL = WEEKDAY_ARRAY.reduce<Record<string, number>>(
  (obj, weekday, index) => Object.assign(obj, { [weekday]: index }),
  {},
);
const WEEKDAYS_DELIMITER = ';';
const WEEKDAY_AND_TIME_DELIMITER = /\s+/;
const WEEKDAY_DELIMITER = '-';
const TIME_PERIOD_DELIMITER = '-';
const TIME_DELIMITER = ':';
const AM = ' AM';
const PM = ' PM';

function parseWeekdayPeriods(rawWeekdayPeriods: string): WeekdayPeriod[] {
  return rawWeekdayPeriods
    .split(WEEKDAYS_DELIMITER)
    .map(weekdayAndTimePeriod => weekdayAndTimePeriod.trim())
    .map(weekdayAndTimePeriod => weekdayAndTimePeriod.split(WEEKDAY_AND_TIME_DELIMITER))
    .flatMap(([weekdayPeriod, timePeriod]: string[]) => {
      const weekdays = parseWeekdayPeriod(weekdayPeriod);
      const formattedTimePeriod = prepareTimePeriodData(timePeriod);
      return createWeekdayAndTimePeriodPairs(weekdays, formattedTimePeriod);
    });
}

function parseWeekdayPeriod(weekdayPeriod: string): string[] {
  const [start, end] = weekdayPeriod.split(WEEKDAY_DELIMITER);

  const startIndex = SHORT_WEEKDAY_TO_NUMERAL[start];
  let weekdayIndices;
  if (end) {
    const endIndex = SHORT_WEEKDAY_TO_NUMERAL[end];
    weekdayIndices = weekdayIndicesRange(startIndex, endIndex);
  } else {
    weekdayIndices = [startIndex];
  }

  return weekdayIndices.map(index => WEEKDAY_ARRAY[index]);
}

function weekdayIndicesRange(startIndex: number, endIndex: number): number[] {
  const weekdayIndices = [startIndex];
  let currentDayIndex = startIndex;
  do {
    currentDayIndex = (currentDayIndex + 1) % WEEKDAY_ARRAY.length;
    weekdayIndices.push(currentDayIndex);
  } while (currentDayIndex !== endIndex);
  return weekdayIndices;
}

function prepareTimePeriodData(timePeriod: string): TimePeriod {
  const [from, to] = timePeriod.split(TIME_PERIOD_DELIMITER);

  const [fromTime, fromTimeFormatted] = getHoursMinutesAndFormatted(from);
  const [toTime, toTimeFormatted] = getHoursMinutesAndFormatted(to);
  const timePeriodFormatted = fromTimeFormatted + ` ${TIME_PERIOD_DELIMITER} ` + toTimeFormatted;

  return {
    from: fromTime,
    to: toTime,
    formatted: timePeriodFormatted,
  };
}

function getHoursMinutesAndFormatted(time: string): [TimeOfDay, string] {
  const [hours, minutes] = time.split(TIME_DELIMITER);
  const timeFormatted = formatTimeAmPm(hours, minutes);
  return [{ hours: +hours, minutes: +minutes }, timeFormatted];
}

function formatTimeAmPm(hours: number | string, minutes: number | string): string {
  const hoursNum = +hours;
  const minutesNum = +minutes;
  const ampm = hoursNum < 12 || hoursNum === 24 ? AM : PM;
  const displayHours = hoursNum % 12 || 12;
  const minutesStr = minutesNum > 9 ? String(minutesNum) : '0' + minutesNum;
  return displayHours + TIME_DELIMITER + minutesStr + ampm;
}

function createWeekdayAndTimePeriodPairs(weekdays: string[], timePeriod: TimePeriod): WeekdayPeriod[] {
  return weekdays.map(weekday => ({ weekday, timePeriod }));
}

function orderFromSunday(weekdayPeriods: WeekdayPeriod[]): WeekdayPeriod[] {
  const startWeekdayIndex = 0; // Sunday
  const orderedWeekdays: WeekdayPeriod[] = [];

  weekdayPeriods.forEach(weekday => {
    const weekdayIndex = WEEKDAY_TO_NUMERAL[weekday.weekday];
    let order = weekdayIndex - startWeekdayIndex;
    if (order < 0) {
      order += WEEKDAY_ARRAY.length;
    }
    orderedWeekdays[order] = weekday;
  });

  return filterEmptyDays(orderedWeekdays);
}

function filterEmptyDays(weekdays: WeekdayPeriod[]): WeekdayPeriod[] {
  return weekdays.filter(x => Boolean(x));
}

function markCurrentWeekday(weekdays: WeekdayPeriod[], today: Date): WeekdayPeriod[] {
  const todayDayName = WEEKDAY_ARRAY[today.getDay()];
  const addIsToday = R.converge(R.assoc('isToday'), [R.propEq(todayDayName, 'weekday'), R.identity]);
  return weekdays.map(weekday => addIsToday(weekday) as WeekdayPeriod);
}

function getOpenTimeLabel(
  operationHours: string | undefined | null,
  nowDate: Date,
  T: TranslateFn,
  timezone: string | undefined | null,
): string {
  if (operationHours === 'Mo-Su 00:00-24:00') {
    return T('ui:Always open');
  }
  if (!operationHours) {
    return '';
  }

  // Freeform hours such as "open daily" or "24/7" cannot be parsed into weekday periods.
  // Without this guard parseAndOrderWeekdays throws on the malformed time segment.
  if (!canParseOperationHours(operationHours)) {
    return '';
  }

  const localizedDate = toLocalDate(nowDate, timezone);

  const weekdayPeriods = parseAndOrderWeekdays(operationHours, localizedDate);
  if (!weekdayPeriods) {
    return '';
  }

  const currentWeekday = findWeekday(weekdayPeriods, localizedDate);

  // No hours for today, or today's closing time has already passed
  if (!currentWeekday || compareDateTimeAndWeekdayTime(localizedDate, currentWeekday.timePeriod.to) === -1) {
    return prepareTimeToNextWorkingDay(localizedDate, weekdayPeriods, T);
  }

  if (isDateInWeekdayOpenHours(localizedDate, currentWeekday)) {
    return prepareTimeToTodayClose(localizedDate, currentWeekday, T);
  }

  return prepareTimeToTodayOpen(localizedDate, currentWeekday, T);
}

function toLocalDate(date: Date, timezone: string | undefined | null): Date {
  const { year, month, day, hour, minute } = DateTime.fromJSDate(date, {
    zone: timezone ?? undefined,
  });
  return new Date(year, month - 1, day, hour, minute);
}

function prepareTimeToNextWorkingDay(nowDate: Date, weekdayPeriods: WeekdayPeriod[], T: TranslateFn): string {
  const tomorrowDate = getNextDayDate(nowDate);
  const nextWeekday = findWeekday(weekdayPeriods, tomorrowDate);

  if (nextWeekday) {
    const minutesToTomorrowOpen = minutesBetweenDateTimeAndNextWeekdayTime(nowDate, nextWeekday.timePeriod.from);
    if (minutesToTomorrowOpen >= 60) {
      return formatClosedUntilWeekday(nextWeekday, T);
    } else {
      return formatOpeningInMinutes(minutesToTomorrowOpen, T);
    }
  } else {
    const nextWorkingWeekday = findNextWorkingWeekday(weekdayPeriods, nowDate);
    return nextWorkingWeekday ? formatClosedUntilWeekday(nextWorkingWeekday, T) : '';
  }
}

function prepareTimeToTodayClose(nowDate: Date, currentWeekday: WeekdayPeriod, T: TranslateFn): string {
  const minutesToClose = minutesBetweenDateTimeAndWeekdayTime(nowDate, currentWeekday.timePeriod.to);
  if (minutesToClose >= 60) {
    return formatOpenUntilHours(currentWeekday, T);
  } else {
    return formatClosingInMinutes(minutesToClose, T);
  }
}

function prepareTimeToTodayOpen(nowDate: Date, currentWeekday: WeekdayPeriod, T: TranslateFn): string {
  const minutesToOpen = minutesBetweenDateTimeAndWeekdayTime(nowDate, currentWeekday.timePeriod.from);
  if (minutesToOpen >= 60) {
    return formatClosedUntilHours(currentWeekday, T);
  } else {
    return formatOpeningInMinutes(minutesToOpen, T);
  }
}

function isDateInWeekdayOpenHours(date: Date, weekday: WeekdayPeriod): boolean {
  const { from, to } = weekday.timePeriod;
  return isInTimePeriod(date.getHours(), date.getMinutes(), from.hours, from.minutes, to.hours, to.minutes);
}

function getNextDayDate(date: Date): Date {
  const nextDate = new Date(date);
  nextDate.setDate(nextDate.getDate() + 1);
  return nextDate;
}

function compareDateTimeAndWeekdayTime(date: Date, weekdayHours: TimeOfDay): number {
  const minutesBetween = minutesBetweenDateTimeAndWeekdayTime(date, weekdayHours);
  if (minutesBetween === 0) {
    return 0;
  }
  if (minutesBetween > 0) {
    return 1;
  }
  return -1;
}

/*
 * important
 * use it to calculate minutes between date time and weekday time of the same day only
 */
function minutesBetweenDateTimeAndWeekdayTime(dateTime: Date, weekdayTime: TimeOfDay): number {
  const dateTimeMinutes = dateTime.getHours() * 60 + dateTime.getMinutes();
  const weekdayTimeMinutes = weekdayTime.hours * 60 + weekdayTime.minutes;
  return weekdayTimeMinutes - dateTimeMinutes;
}

/*
 * important
 * use it to calculate minutes between date time and weekday time of the next day only
 */
function minutesBetweenDateTimeAndNextWeekdayTime(dateTime: Date, weekdayTime: TimeOfDay): number {
  const dateTimeMinutes = dateTime.getHours() * 60 + dateTime.getMinutes();
  const weekdayTimeMinutes = weekdayTime.hours * 60 + weekdayTime.minutes;
  const midnightMinutes = 24 * 60;
  return weekdayTimeMinutes + (midnightMinutes - dateTimeMinutes);
}

const formatOpeningInMinutes = (minutesToOpen: number, T: TranslateFn): string =>
  T('ui:Opening in _minutes_', { count: minutesToOpen });
const formatClosingInMinutes = (minutesToClose: number, T: TranslateFn): string =>
  T('ui:Closing in _minutes_', { count: minutesToClose });

const formatClosedUntilHours = (weekday: WeekdayPeriod, T: TranslateFn): string => {
  const { hours, minutes } = weekday.timePeriod.from;
  const formattedTime = formatTimeAmPm(hours, minutes);
  return T('ui:Closed until _time_', { time: formattedTime });
};

const formatOpenUntilHours = (weekday: WeekdayPeriod, T: TranslateFn): string => {
  const { hours, minutes } = weekday.timePeriod.to;
  const formattedTime = formatTimeAmPm(hours, minutes);
  return T('ui:Open until _time_', { time: formattedTime });
};

const formatClosedUntilWeekday = (weekday: WeekdayPeriod, T: TranslateFn): string => {
  const { hours, minutes } = weekday.timePeriod.from;
  const formattedTime = `${weekday.weekday} ${formatTimeAmPm(hours, minutes)}`;
  return T('ui:Closed until _time_', { time: formattedTime });
};

const findNextWorkingWeekday = (weekdayPeriods: WeekdayPeriod[], nowDate: Date): WeekdayPeriod | undefined => {
  let startIndex = (nowDate.getDay() + 1) % 7;
  while (!findWeekdayByDayIndex(weekdayPeriods, startIndex)) {
    startIndex = (startIndex + 1) % 7;
  }

  return findWeekdayByDayIndex(weekdayPeriods, startIndex);
};
